import React, { Fragment } from 'react';
import Helmet from 'react-helmet';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { compose, defaultProps, withProps } from 'recompose';
import { get, map } from 'lodash';
import { useLocation } from 'react-router-dom';
import { withSegmentContext } from '@sparefoot/react-core/segment';
import Crumb from './Crumb';

import './Breadcrumbs.scss';


function createCrumbSchema(crumbs) {
	return {
		'@context': 'http://schema.org',
		'@type': 'BreadcrumbList',
		itemListElement: crumbs
		.filter((crumb) => crumb.props.label)
		.map((crumb, index) => (
			{
				'@type': 'ListItem',
				position: index + 1,
				item: {
					'@id': get(crumb, 'props.id', get(crumb, 'props.to', '')).split('?').shift(),
					name: crumb.props.name || crumb.props.label
				}
			}
		))
	};
}

export const enhance = compose(
	withSegmentContext,
	defaultProps({ location: {}, searchType: 'storage' }),
	withProps(({
		location, crumbs = [], cityUrl, stateUrl, searchType, tertiary
	}) => {
		const newCrumbs = [];
		const { state, fullState, subLocality, city } = location;
		const sub = subLocality || city;
		const route = useLocation();
		let storagePrefix;

		if (route.pathname.includes('boat')) {
			storagePrefix = 'Boat ';
		} else if (route.pathname.includes('car')) {
			storagePrefix = 'Car ';
		} else if (route.pathname.includes('climate')) {
			storagePrefix = 'Climate Controlled ';
		} else if (route.pathname.includes('24')) {
			storagePrefix = '24 Hour ';
		} else {
			storagePrefix = searchType === 'vehicle' ? 'Vehicle ' : 'Self-';
		}

		newCrumbs.push(
			<Crumb
				to="/"
				id="/self-storage/"
				key="base"
				label="Find Storage"
				name="Locations"
				segmentLabel="/"
				tertiary={tertiary}
			/>
		);

		if (stateUrl) {
			newCrumbs.push(
				<Crumb
					key="state"
					to={stateUrl}
					isLast={!cityUrl && !crumbs.length}
					label={fullState}
					name={state}
					className="state"
					segmentLabel={stateUrl}
					segmentProperties={{ breadcrumb: fullState }}
					tertiary={tertiary}
				/>
			);
		}

		if (cityUrl) {
			const isLast = !crumbs.length;
			newCrumbs.push(
				<Crumb
					key="sublocality"
					className="sublocality"
					to={cityUrl}
					isLast={isLast}
					label={`${sub} Storage`}
					name={sub}
					segmentLabel={cityUrl}
					segmentProperties={{ breadcrumb: `${sub} Storage` }}
				>
					{sub} <span className="self-storage">{storagePrefix}Storage</span>
				</Crumb>
			);
		}

		return {
			crumbs: [...newCrumbs, ...crumbs],
			crumbJSON: createCrumbSchema([...newCrumbs, ...crumbs])
		};
	})
);

export function Breadcrumbs({
	crumbs, crumbJSON, className
}) {
	return (
		<Fragment>
			<ul className={classnames('ss-breadcrumbs', className)}>
				{map(crumbs, (crumb) => (
					<li className="ss-breadcrumb" key={crumb.key}>
						{crumb}
					</li>
				))}
			</ul>
			<Helmet>
				<script type="application/ld+json">
					{JSON.stringify(crumbJSON)}
				</script>
			</Helmet>
		</Fragment>
	);
}

Breadcrumbs.propTypes = {
	className: PropTypes.string,
	crumbJSON: PropTypes.object,
	crumbs: PropTypes.node
};

export default enhance(Breadcrumbs);
